import { getLogInAsHeaders, LogInAsHeaderNames } from '@fusion/saas-components-v3'
import type {
  Collaborator,
  Device,
  DeviceData,
  DeviceRegistered,
  DeviceSummaryItem,
  DeviceSubscriptionPatchRequest,
  ModelMetadata,
  PostCollaboratorRequest,
  RegisterDeviceRequest,
  UpdateDeviceRequest,
  ConfigData,
  StdConfig
} from '@/types/devices'
import { CachePolicy } from './utils'
import { DELETE, GET, GET_STREAM, PATCH, POST } from './helpers'

export const getDevices = async function (includePublic = false) {
  const publicQuery = includePublic ? '?public=true' : ''
  return GET<DeviceSummaryItem[]>(
    `/summary/devices${publicQuery}`,
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID, LogInAsHeaderNames.SAP_ID])
    },
    { cache: CachePolicy.NoStore },
    APP_ENV.VITE_APP_DEVICES_HOST_V3
  )
}

export const getUserDevices = async function () {
  return GET<DeviceData[]>(
    '/devices',
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID, LogInAsHeaderNames.SAP_ID])
    },
    {},
    APP_ENV.VITE_APP_DEVICES_HOST_V3
  )
}

export const getDevice = function (deviceId: string) {
  return GET<Device>(
    `/devices/${deviceId}`,
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID])
    },
    {},
    APP_ENV.VITE_APP_DEVICES_HOST_V3
  )
}

export const postRegisterDevice = async function (deviceData: RegisterDeviceRequest) {
  const body = JSON.stringify(deviceData)
  return POST<DeviceRegistered>(
    '/devices/register',
    body,
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID, LogInAsHeaderNames.SAP_ID])
    },
    {},
    APP_ENV.VITE_APP_DEVICES_HOST_V3
  )
}

export const postUnregisterDevice = function (deviceId: string) {
  return POST(
    `/devices/${deviceId}/unregister`,
    null,
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID, LogInAsHeaderNames.SAP_ID])
    },
    {},
    APP_ENV.VITE_APP_DEVICES_HOST_V3
  )
}

export const patchUpdateDevice = function (
  deviceData: UpdateDeviceRequest,
  deviceId: string
) {
  const body = JSON.stringify(deviceData)
  return PATCH<void>(
    `/devices/${deviceId}`,
    body,
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID])
    },
    {},
    APP_ENV.VITE_APP_DEVICES_HOST_V3
  )
}

export const postShareDevice = function (userInfo: PostCollaboratorRequest, deviceId: string) {
  const body = JSON.stringify(userInfo)
  return POST<Collaborator>(
    `/devices/${deviceId}/collaborators`,
    body,
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID])
    },
    {},
    APP_ENV.VITE_APP_DEVICES_HOST_V3
  )
}

export const getDeviceCollaborators = function (deviceId: string) {
  return GET<Collaborator[]>(
    `/devices/${deviceId}/collaborators`,
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID])
    },
    {},
    APP_ENV.VITE_APP_DEVICES_HOST_V3
  )
}

export const deleteDeviceCollaborator = function (collaborator: string, deviceId: string) {
  return DELETE(
    `/devices/${deviceId}/collaborators/${collaborator}`,
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID])
    },
    {},
    APP_ENV.VITE_APP_DEVICES_HOST_V3
  )
}

export const getDeviceConfig = function (deviceId: string) {
  return GET<StdConfig>(
    `/devices/${deviceId}/config`,
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID])
    },
    {},
    APP_ENV.VITE_APP_DEVICES_HOST_V3
  )
}

export const postDeviceConfig = function (config: ConfigData, deviceId: string) {
  const body = JSON.stringify(config)
  return POST<void>(
    `/devices/${deviceId}/config`,
    body,
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID])
    },
    {},
    APP_ENV.VITE_APP_DEVICES_HOST_V3
  )
}

export const getModelsMetaData = function () {
  return GET<ModelMetadata>('/devices/models', {}, {}, APP_ENV.VITE_APP_DEVICES_HOST_V3)
}

export const patchDeviceSubscription = async function (data: DeviceSubscriptionPatchRequest) {
  const body = JSON.stringify(data)
  return PATCH<void>(
    '/devices/subscription',
    body,
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID, LogInAsHeaderNames.SAP_ID])
    },
    {},
    APP_ENV.VITE_APP_DEVICES_HOST_V3
  )
}

export const getDeviceStateStream = function (deviceId: string) {
  return GET_STREAM(
    `/devices/${deviceId}/state/stream`,
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID])
    },
    {},
    APP_ENV.VITE_APP_DEVICES_HOST_V3
  )
}
